'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import axios from 'axios';
import { useTranslation } from '@/context/LanguageContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faChevronRight, faFolder, faArrowRight, faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { RefPpidItem } from '@/types/ppid';
import { ApiResponse } from '@/types/api';

export default function PpidContent() {
  const { t, language } = useTranslation();
  const [categories, setCategories] = useState<RefPpidItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await axios.post<ApiResponse<RefPpidItem[]>>('/api/doServices', {
          method: 'GET',
          endpoint: 'web/ref_ppid?limit=10&page=1'
        });

        if (response.data?.data) {
          setCategories(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching PPID categories:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Color palette for cards
  const colors = [
    { bg: 'bg-gradient-to-br from-unmulGreen to-emerald-700', icon: 'bg-white/20' },
    { bg: 'bg-gradient-to-br from-blue-600 to-blue-800', icon: 'bg-white/20' },
    { bg: 'bg-gradient-to-br from-purple-600 to-purple-800', icon: 'bg-white/20' },
    { bg: 'bg-gradient-to-br from-orange-500 to-orange-700', icon: 'bg-white/20' },
    { bg: 'bg-gradient-to-br from-teal-600 to-teal-800', icon: 'bg-white/20' },
  ];

  return (
    <>
      {/* Hero */}
      <header className="pt-36 pb-16 bg-gradient-to-br from-unmulDarkGreen to-unmulGreen text-white">
        <div className="container mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-green-200 mb-4">
            <Link href="/"><FontAwesomeIcon icon={faHome} /></Link>
            <FontAwesomeIcon icon={faChevronRight} className="text-xs" />
            <span className="text-unmulYellow">PPID</span>
          </nav>
          <h1 className="text-4xl md:text-5xl font-serif font-bold" data-aos="fade-up">
            {language === 'id' ? 'Pejabat Pengelola' : 'Public Information'}
            <span className="text-unmulYellow block mt-2">
              {language === 'id' ? 'Informasi & Dokumentasi' : 'Management Officer'}
            </span>
          </h1>
          <p className="text-green-100 mt-4 max-w-2xl">
            {language === 'id' 
              ? 'Layanan informasi publik sesuai dengan Undang-Undang No. 14 Tahun 2008 tentang Keterbukaan Informasi Publik.'
              : 'Public information service in accordance with Law No. 14 of 2008 concerning Public Information Disclosure.'}
          </p>
        </div>
      </header>

      {/* Categories Grid */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-serif font-bold text-gray-800 mb-4">
              {language === 'id' ? 'Kategori Informasi' : 'Information Categories'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {language === 'id' 
                ? 'Pilih kategori informasi yang ingin Anda akses'
                : 'Select the information category you want to access'}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-unmulGreen"></div>
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              <FontAwesomeIcon icon={faFileAlt} className="text-5xl mb-4 text-gray-300" />
              <p>{language === 'id' ? 'Tidak ada kategori tersedia' : 'No categories available'}</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {categories.map((category, index) => {
                const colorSet = colors[index % colors.length];
                const title = language === 'id' ? category.title_idn : category.title_en;
                
                return (
                  <Link
                    key={category.id}
                    href={`/ppid/${category.slug}`}
                    className="group block"
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                  >
                    <div className={`${colorSet.bg} p-8 rounded-2xl text-white shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 h-full`}>
                      <div className={`w-16 h-16 ${colorSet.icon} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition`}>
                        <FontAwesomeIcon icon={faFolder} className="text-2xl" />
                      </div>
                      <h3 className="text-xl font-bold mb-3 leading-tight">{title}</h3>
                      <div className="flex items-center text-white/80 text-sm font-medium group-hover:text-white transition">
                        <span>{language === 'id' ? 'Lihat Informasi' : 'View Information'}</span>
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2 group-hover:translate-x-2 transition" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-gray-800 mb-4">
              {language === 'id' ? 'Tentang PPID' : 'About PPID'}
            </h3>
            <p className="text-gray-600 leading-relaxed">
              {language === 'id' 
                ? 'PPID (Pejabat Pengelola Informasi dan Dokumentasi) adalah pejabat yang bertanggung jawab di bidang penyimpanan, pendokumentasian, penyediaan, dan/atau pelayanan informasi di badan publik sesuai dengan Undang-Undang Keterbukaan Informasi Publik.'
                : 'PPID (Public Information and Documentation Management Officer) is the official responsible for storing, documenting, providing, and/or serving information in public bodies in accordance with the Public Information Disclosure Law.'}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
